import _ from 'lodash';
import { ExtendedUser, EmptyExtendedUser, User } from '../Types';
import {
  Period,
  WeeklyPeriod,
  MonthlyPeriod,
  CoachingStatsSummaryForPeriod,
  CoachingStatsAssociateDetailsOutput,
  GetCoachingStatsResponse,
  CoachingStatsAssociateType,
  CoachingStatsAssociateStatus,
} from '@amzn/aet-coachingstats-kit';
import {
  CoachingStatsAssociateDetailsOutputExtended,
  CoachingStatsSummaryForPeriodExtended,
  TMCoachingStatsSummary,
  BasicStats,
  TMCoachingStatsSummaryInternal,
} from '../Types/CoachingStats';

import { getUserIdWithPrefix } from './Helper';

const isSamePeriod = (sourcePeriod: Period, targetPeriod: Period) => {
  return (
    // Same period type
    sourcePeriod.periodType === targetPeriod.periodType &&
    // Same year
    sourcePeriod.year === targetPeriod.year &&
    // Same month (Equality check is performed using '==' and not '===' to consider null and undefined as the same)
    (sourcePeriod as MonthlyPeriod).month ==
      (targetPeriod as MonthlyPeriod).month &&
    // Same week  (Equality check is performed using '==' and not '===' to consider null and undefined as the same)
    (sourcePeriod as WeeklyPeriod).week == (targetPeriod as WeeklyPeriod).week
  );
};

export const getExtendedAssociateDetails = (
  associateDetails: CoachingStatsAssociateDetailsOutput[],
  directReportsMap: Record<string, ExtendedUser>
): CoachingStatsAssociateDetailsOutputExtended[] => {
  const extendedAssociateDetails = associateDetails.map(item => {
    const userIdFileds = item.userId.split(':');
    const userIdWithoutPrefix = userIdFileds[userIdFileds.length - 1];
    return {
      ...item,
      extendedUserInfo:
        item.userId in directReportsMap
          ? directReportsMap[item.userId]
          : {
              ...EmptyExtendedUser,
              alias: item.alias ?? '',
              userID: userIdWithoutPrefix,
            },
    };
  });

  return extendedAssociateDetails;
};

export const getBasicStatsForCoaching = (
  associateDetails: CoachingStatsAssociateDetailsOutputExtended[],
  associateType?: CoachingStatsAssociateType
): BasicStats => {
  const allTargetAssociates = associateDetails.filter(item =>
    associateType ? item.associateType === associateType : true
  );

  const associatesRequiringCoaching = allTargetAssociates.filter(
    item => item.coachingRequirement > item.coachings
  );

  const totalCoachingRequired = _.chain(allTargetAssociates)
    .map(item => item.coachingRequirement)
    .sum()
    .value();

  const completedCoachings = _.chain(allTargetAssociates)
    .map(item => Math.min(item.coachingRequirement, item.coachings))
    .sum()
    .value();

  const remainingCoachings = totalCoachingRequired - completedCoachings;

  const totalContactReviews = _.chain(allTargetAssociates)
    .map(item => item.contactReviews)
    .sum()
    .value();

  return {
    total: allTargetAssociates.length,
    remaining: associatesRequiringCoaching.length,
    remainingAssociateDetails: associatesRequiringCoaching,
    associateDetails: allTargetAssociates,

    totalCoachingRequired,
    remainingCoachings,
    completedCoachings,

    totalContactReviews,
  };
};

const getBasicStatsForContactReviews = (
  associateDetails: CoachingStatsAssociateDetailsOutputExtended[]
): BasicStats => {
  const allTargetAssociates = associateDetails;

  const totalContactReviews = _.sumBy(allTargetAssociates, 'contactReviews');
  const totalUnopenedContactReviews = _.sumBy(
    allTargetAssociates,
    'unopenedContactReviews'
  );

  const associatesWithUnopenedContactReviews = allTargetAssociates.filter(
    item => item.unopenedContactReviews > 0
  );

  return {
    // TODO: Find a way not to have both total and totalContactReviews which is redundant.
    total: totalContactReviews,
    remaining: totalUnopenedContactReviews,
    remainingAssociateDetails: associatesWithUnopenedContactReviews,
    associateDetails: allTargetAssociates,

    // TODO: find a better way to set these redundant fields (for contact review stats) to zero
    totalCoachingRequired: 0,
    remainingCoachings: 0,
    completedCoachings: 0,

    totalContactReviews,
  };
};

export const getTargetAssociates = (
  inputAssociates: CoachingStatsAssociateDetailsOutput[]
): CoachingStatsAssociateDetailsOutput[] => {
  const targetAssociates = inputAssociates.filter(
    (associate: CoachingStatsAssociateDetailsOutput) =>
      associate.include === true &&
      associate.associateType !==
        CoachingStatsAssociateType.NOT_ACTIVELY_HANDLING_CONTACTS &&
      associate.associateStatus !== CoachingStatsAssociateStatus.TERMINATED
  );

  return targetAssociates;
};

const selectCoachingStatsByPeriod = (
  coachingStatsForPeriods: CoachingStatsSummaryForPeriod[],
  targetPeriod: Period
): CoachingStatsSummaryForPeriod[] => {
  const targetCoachingStats = coachingStatsForPeriods.filter(item =>
    isSamePeriod(item.period, targetPeriod)
  )!;

  return targetCoachingStats;
};

const getExtendedCoachingStatsSummaryForPeriod = (
  targetCoachingStats: CoachingStatsSummaryForPeriod,
  directReportsMap: Record<string, ExtendedUser>
): CoachingStatsSummaryForPeriodExtended => {
  const targetAssociateDetails: CoachingStatsAssociateDetailsOutput[] =
    getTargetAssociates(targetCoachingStats.associateDetails);

  const extendedTargetAssociateDetails = getExtendedAssociateDetails(
    targetAssociateDetails,
    directReportsMap
  );

  const bottomQuartile = getBasicStatsForCoaching(
    extendedTargetAssociateDetails,
    CoachingStatsAssociateType.BOTTOM_QUARTILE
  );

  const newHire = getBasicStatsForCoaching(
    extendedTargetAssociateDetails,
    CoachingStatsAssociateType.NEW_HIRE
  );

  /**
   * If this method is called with CoachingStatsAssociateStatus.MONTHLY
   * Then this would be the contact reviews for associates with monthly coaching requirements
   *
   * If this method is called with CoachingStatsAssociateStatus.WEEKLY
   * Then this would be the contact reviews for associates with weekly coaching requirements
   */
  const contactReviewsForAssociatesWithPeriodRequirement =
    getBasicStatsForContactReviews(extendedTargetAssociateDetails);

  /**
   * This is used to get the contact reviews for ALL associates in the target time interval (weekly or monthly)
   */
  const contactReviewForAllAssociatesOverPeriod =
    getBasicStatsForContactReviews(
      getExtendedAssociateDetails(
        targetCoachingStats.associateDetails,
        directReportsMap
      )
    );

  const allAssociateTypes = getBasicStatsForCoaching(
    extendedTargetAssociateDetails
  );

  const extendedCoachingStatsSummary: CoachingStatsSummaryForPeriodExtended = {
    ...targetCoachingStats,
    associateDetails: extendedTargetAssociateDetails,
    basicStats: {
      newHire,
      bottomQuartile,
      allAssociateTypes,
      contactReviewsForAssociatesWithPeriodRequirement,
      contactReviewForAllAssociatesOverPeriod,
    },
  };
  return extendedCoachingStatsSummary;
};

export const getTMCoachingStatsSummaryInternal = (
  weeklyCoachingStatsList: CoachingStatsSummaryForPeriod[],
  monthlyCoachingStats: CoachingStatsSummaryForPeriod,
  directReportsMap: Record<string, ExtendedUser> = {}
): TMCoachingStatsSummaryInternal => {
  const extendedWeeklyCoachingSummaries = weeklyCoachingStatsList.map(
    weeklyCoachingStats =>
      getExtendedCoachingStatsSummaryForPeriod(
        weeklyCoachingStats,
        directReportsMap
      )
  );
  const extendedMonthlyCoachingSummary =
    getExtendedCoachingStatsSummaryForPeriod(
      monthlyCoachingStats,
      directReportsMap
    );

  return {
    extendedWeeklyCoachingSummaries,
    extendedMonthlyCoachingSummary,
  };
};

export const getTMCoachingStatsSummary = (
  currentWeekPeriod: WeeklyPeriod,
  currentMonthPeriod: MonthlyPeriod,
  directReportsMap: Record<string, ExtendedUser>,
  coachingStats: GetCoachingStatsResponse
): TMCoachingStatsSummary | undefined => {
  const weeklyCoachingStatsList = selectCoachingStatsByPeriod(
    coachingStats.coachingStatsForPeriods,
    currentWeekPeriod
  );

  if (weeklyCoachingStatsList.length !== 1) {
    throw new Error(
      `The given weekly period is not found from the response of getCoachingStats: ${JSON.stringify(
        currentWeekPeriod
      )}`
    );
  }

  const monthlyCoachingStats = _.first(
    selectCoachingStatsByPeriod(
      coachingStats.coachingStatsForPeriods,
      currentMonthPeriod
    )
  )!; // There is 1 and only 1 monthly coaching stats in TM use case.

  if (!monthlyCoachingStats) {
    throw new Error(
      `The given monthly period is not found from the response of getCoachingStats: ${JSON.stringify(
        currentMonthPeriod
      )}`
    );
  }

  const tmCoachingStatsSummaryInternalResult =
    getTMCoachingStatsSummaryInternal(
      weeklyCoachingStatsList,
      monthlyCoachingStats,
      directReportsMap
    );

  return {
    extendedWeeklyCoachingSummary:
      tmCoachingStatsSummaryInternalResult.extendedWeeklyCoachingSummaries[0], // TM use case has only 1 weekly coaching stats
    extendedMonthlyCoachingSummary:
      tmCoachingStatsSummaryInternalResult.extendedMonthlyCoachingSummary,
  };
};
